<template>
  <div>
    <div id="app">
      <div id="print"></div>
      <div id="web">
        <router-view></router-view>
        <CookiesBanner />
        <Loading v-if="isAppLoading"></Loading>
        <LoadingEdc ref="loadingEdc" v-if="isEdcProcessing" :timeoutMinutes="edcTimeoutMinutes" />

        <Dialog ref="Alert"></Dialog>

        <!-- Idle screen lock (spec 09). Setup comes first: a user with no PIN
             has nothing to unlock the overlay with, so showing the lock over
             the setup screen would be a dead end. -->
        <PinSetupDialog
          v-if="showPinSetup"
          :forced="true"
          @saved="onPinSaved"
        />
        <LockOverlay
          v-else-if="isLocked"
          @unlocked="onUnlocked"
          @switch-account="onSwitchAccount"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CookiesBanner from "./components/form/CookiesBanner.vue";
import Dialog from "@/components/modal/Dialog";
import { eventBus } from "@/main";
import Loading from "./components/Loading.vue";
import LoadingEdc from "@/components/LoadingEdc.vue";
import LockOverlay from "@/components/lock/LockOverlay.vue";
import PinSetupDialog from "@/components/lock/PinSetupDialog.vue";
import errorService from "@/services/ErrorService";
import { createIdleLock, clearLockState } from "@/utils/idleLock";

export default {
  name: "App",
  components: {
    CookiesBanner,
    Dialog,
    Loading,
    LoadingEdc,
    LockOverlay,
    PinSetupDialog,
  },
  created() {
    // Set up error service reference to this app instance for UI interactions
    errorService.setAppReference(this);
    
    // Maintain backward compatibility for existing eventBus error emissions
    // This will be phased out as stores are refactored
    eventBus.$on("apiError", err => {
      errorService.handleError(err, { source: 'eventBus_legacy' });
    });
    eventBus.$on(
      "alertToast",
      ({ message, variant = "danger", noAutoHide = false }) => {
        this.$refs.Alert.showToast(variant, message, noAutoHide);
      }
    );
    eventBus.$on(
      "alertSwal",
      ({ title, message, icon = "info", callback = () => {} }) => {
        this.$refs.Alert.showAlertInfo(title, message, icon).then(callback);
      }
    );
    eventBus.$on(
      "confirmSwal",
      ({ title, message, icon = "warning", callback = () => {} }) => {
        this.$refs.Alert.showAlertConfirm(title, message, icon).then(callback);
      }
    );
    eventBus.$on("appLoadingStatus", e => {
      this.isAppLoading = e;
    });
    eventBus.$on("edcProcessingStatus", e => {
      this.isEdcProcessing = e;
    });
    eventBus.$on("setEdcTimeout", timeoutMinutes => {
      this.edcTimeoutMinutes = timeoutMinutes;
    });
    eventBus.$on("showSpinnerToast", () => {
      this.$refs.Alert.showToastWithSpinner();
    });
    eventBus.$on("hideSpinnerToast", () => {
      this.$refs.Alert.hideToast();
    });
  },
  mounted() {
    // Hide loading screen once app is mounted
    const loadingScreen = document.getElementById("app-loading");
    if (loadingScreen) {
      loadingScreen.classList.add("app-loading-hidden");
      // Remove from DOM after transition
      setTimeout(() => {
        loadingScreen.remove();
      }, 300);
    }
  },
  data() {
    return {
      isAppLoading: false,
      isEdcProcessing: false,
      edcTimeoutMinutes: 5, // Default 5 minutes
      idleLock: null,
      isLocked: false,
    };
  },
  computed: {
    idleLockMinutes() {
      return this.$store.getters["moduleUser/getIdleLockMinutes"];
    },
    showPinSetup() {
      return this.$store.getters["moduleUser/getNeedsPinSetup"];
    },
  },
  watch: {
    // /auth/context is re-read at login and on every clinic switch, so the
    // timeout can change under a live session. Rebuilding the tracker is
    // cheaper and less error-prone than mutating its timeout in place.
    idleLockMinutes: {
      immediate: true,
      handler() {
        this.restartIdleLock();
      },
    },
  },
  beforeDestroy() {
    if (this.idleLock) this.idleLock.stop();
  },
  methods: {
    restartIdleLock() {
      if (this.idleLock) this.idleLock.stop();
      this.idleLock = null;
      this.isLocked = false;

      if (!this.idleLockMinutes) {
        clearLockState();
        return;
      }

      this.idleLock = createIdleLock({
        timeoutMinutes: this.idleLockMinutes,
        onLock: () => {
          this.isLocked = true;
        },
        onUnlock: () => {
          this.isLocked = false;
        },
        // Unattended screens (the patient waiting-room display) must not put a
        // staff lock screen in front of patients.
        isSuspended: () => Boolean(this.$route && this.$route.meta.noIdleLock),
      });
      this.idleLock.start();
    },
    onUnlocked() {
      if (this.idleLock) this.idleLock.unlock();
      else this.isLocked = false;
    },
    onSwitchAccount() {
      if (this.idleLock) this.idleLock.stop();
      this.idleLock = null;
      this.isLocked = false;
      clearLockState();
      if (this.$route.path !== "/login") this.$router.push("/login");
    },
    onPinSaved() {
      // getNeedsPinSetup flips off via the store's hasPin, so the dialog
      // closes on its own; the tracker starts now that unlocking is possible.
      this.restartIdleLock();
    },
  },
};
</script>